// utils/debugLayerSwitch.js
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";
import { log } from '@libs/utils/helper.js';
import { APP_CONFIG, LOG_MODULES} from '@config/config';

const debugLayerSwitch=LOG_MODULES.SWITCHLAYER;

// Main diagnostics wiring
export async function wireLayerDiagnostics(
  map,
  view,
  {
    imageLayerId   = "Wells-Image",
    featureLayerId = "Wells-Feature"
  } = {}
) {

  const image = map.findLayerById(imageLayerId);
  const feature = map.findLayerById(featureLayerId);

  if (!image || !feature) {
    log(debugLayerSwitch,"[LayerDiag] Layer(s) not found:", { image, feature });
    return;
  }

  // Resolve LayerViews (needed for suspended + updating)
  const [imgLV, flLV] = await Promise.all([
    view.whenLayerView(image),
    view.whenLayerView(feature)
  ]);

  log(debugLayerSwitch,"[LayerDiag] Diagnostics initialized for:", {
    imageLayerId,
    featureLayerId
  });

  // ────────────────────────────────────────────────────────────────
  // 1) Scale transition watcher (fires only when state changes)
  // ────────────────────────────────────────────────────────────────
  let prevState = null;

  view.watch("scale", (scale) => {
    const imgIn = inScaleRange(image, scale);
    const flIn  = inScaleRange(feature, scale);
    const state = `${imgIn}|${flIn}`;

    if (state === prevState) return;
    prevState = state;

    const label =
      imgIn && !flIn  ? "MapImage ACTIVE / Feature INACTIVE" :
      !imgIn && flIn  ? "Feature ACTIVE / MapImage INACTIVE" :
      imgIn && flIn   ? "⚠️ BOTH ACTIVE (scale overlap)" :
                        "⚠️ BOTH INACTIVE (scale gap)";

    log(debugLayerSwitch, `[LayerDiag] scale=${Math.round(view.scale).toLocaleString()}, zoom=${view.zoom} → ${label}`);
   

    log(debugLayerSwitch, logLayerSnapshot(image, feature, imgLV, flLV,view.zoom));
  });

  // ────────────────────────────────────────────────────────────────
  // 2) LayerView state watchers (suspended + updating)
  // ────────────────────────────────────────────────────────────────
  const watchLV = (lv, name) => {
    ["suspended", "updating"].forEach((prop) => {
      reactiveUtils.watch(
        () => lv[prop],
        (val) => {
           const zoom=Math.round(view.zoom);
           const niceScale = niceScaleLabel(view.scale, 2);
           console.error("map the scale and zoom level:", niceScale, zoom);
          log(debugLayerSwitch,
            `[LayerDiag] ${name}.LayerView.${prop} =`, val,
            `| scale=${niceScale}`,
            `| zoom=${zoom}`
          );
        }
      );
    });
  };

  watchLV(imgLV, "MapImageLayer");
  watchLV(flLV, "FeatureLayer");

  // ────────────────────────────────────────────────────────────────
  // 3) Snapshot table
  // ────────────────────────────────────────────────────────────────
  

  // ────────────────────────────────────────────────────────────────
  // 4) Manual console snapshot
  // ────────────────────────────────────────────────────────────────
  window.__layerDiag = () =>logLayerSnapshot(image, feature, imgLV, flLV,view.zoom);
  log(debugLayerSwitch,"[LayerDiag] Tip: run __layerDiag() in console anytime for current layer state");
}


// Helper: logical scale-range evaluation
export function inScaleRange(layer, scale) {
  const min = layer.minScale ?? 0;
  const max = layer.maxScale ?? 0;

  const aboveMax = max === 0 || scale >= max;
  const belowMin = min === 0 || scale <= min;

  return aboveMax && belowMin;
}
/**
 * Rounds a scale value to N significant figures and formats it
 * with underscore separators, e.g. 36894790.67 → "37_000_000"
 */
function niceScaleLabel(value, sigFigs = 2) {
  if (value === 0) return "0";

  const magnitude = Math.pow(10, Math.floor(Math.log10(Math.abs(value))) - (sigFigs - 1));
  const rounded = Math.round(value / magnitude) * magnitude;

  return rounded.toString().replace(/\B(?=(\d{3})+(?!\d))/g, "_");
}
function logLayerSnapshot(img, fl, imgLV, flLV,zoom) {
    console.table({
      MapImageLayer: {
        visible: img.visible,
        loadStatus: img.loadStatus,
        suspended: imgLV.suspended,
        updating: imgLV.updating,
        minScale: img.minScale,
        maxScale: img.maxScale,
        zoom: zoom
      },
      FeatureLayer: {
        visible: fl.visible,
        loadStatus: fl.loadStatus,
        suspended: flLV.suspended,
        updating: flLV.updating,
        minScale: fl.minScale,
        maxScale: fl.maxScale,
        zoom: zoom
      }
    });
  }